// Keep decorative video behind the poster until it is visible and allowed to play.
export default function initMedia() {
    const videos = Array.from(document.querySelectorAll('video[data-ss-video]'));
    if (!videos.length || !window.IntersectionObserver) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 700px)');
    const touch = window.matchMedia('(hover: none)');
    const connection = navigator.connection;
    const states = videos.map(video => ({
        video,
        panel: video.closest('[data-ss-video-hover]'),
        visible: false,
        hovered: false,
        focused: false,
        pending: false,
        loaded: false,
    }));

    function wanted(state) {
        if (reduced.matches || connection?.saveData || document.hidden || !state.visible) return false;
        if (state.video.hasAttribute('data-ss-video-desktop') && mobile.matches) return false;
        return !state.panel || state.hovered || state.focused
            || (touch.matches && state.video.hasAttribute('data-ss-video-touch'));
    }

    function show(state, playing) {
        state.video.classList.toggle('playing', playing);
        if (state.panel) state.panel.classList.toggle('playing', playing);
    }

    function sync(item) {
        const state = item;
        const { video } = state;
        if (!wanted(state)) {
            if (!video.paused) video.pause();
            show(state, false);
            return;
        }
        if (state.pending || !video.paused) return;

        if (!state.loaded) {
            if (video.dataset.src) video.src = video.dataset.src;
            video.querySelectorAll('source[data-src]').forEach(source => {
                source.setAttribute('src', source.dataset.src);
            });
            video.load();
            state.loaded = true;
        }
        video.muted = true;
        state.pending = true;
        const result = video.play();
        Promise.resolve(result).then(() => {
            state.pending = false;
            if (wanted(state)) {
                show(state, true);
            } else {
                video.pause();
                show(state, false);
            }
        }).catch(() => {
            state.pending = false;
            show(state, false);
        });
    }

    const syncAll = () => states.forEach(sync);
    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            const state = states.find(item => item.video === entry.target);
            state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
            sync(state);
        });
    }, { threshold: [0, 0.15] });

    states.forEach(item => {
        const state = item;
        if (state.panel) {
            state.panel.addEventListener('mouseenter', () => { state.hovered = true; sync(state); });
            state.panel.addEventListener('mouseleave', () => { state.hovered = false; sync(state); });
            state.panel.addEventListener('focusin', () => { state.focused = true; sync(state); });
            state.panel.addEventListener('focusout', event => {
                state.focused = state.panel.contains(event.relatedTarget);
                sync(state);
            });
        }
        observer.observe(state.video);
    });

    document.addEventListener('visibilitychange', syncAll);
    [reduced, mobile, touch].forEach(query => query.addEventListener('change', syncAll));
    if (connection?.addEventListener) connection.addEventListener('change', syncAll);
}
